/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { Fragment, useContext, useEffect, useState } from "react";
import { TextField, MenuItem, Select } from "@mui/material";
import { useTranslation } from "react-i18next";
import LabelledField from "components/labelled-field";
import { TabPanel } from "cms/images/image-selector/dialog-edit/panels/tab-panel";
import { isStringNotEmpty } from "utils/empty-exists";
import { DocumentEditContext } from "cms/documents/document-selector/context/document-edit-context";

const DocumentEditPropertiesPanel = () => {
    const { t }  = useTranslation();
    
    const {
        hasDocument,
        selectedDocument,
        localState,
        setLocalState,
        setDocument,
        selectedDocEditTab,
        setHasChanges,
        setIsFieldValid
    } = useContext(DocumentEditContext);
    
    const {
        title: _title = "",
        description: _description = "",
        link_text: _link_text = "",
        type: _type,
        audience: _audience,
        language: _language,
    } = selectedDocument || {};
    
    const [title,setTitle] = useState(isStringNotEmpty(_title) ? _title : "" );
    const [description,setDescription] = useState(isStringNotEmpty(_description) ? _description : "");
    const [linkText,setLinkText] = useState(isStringNotEmpty(_link_text) ? _link_text : "");
    const [language,setLanguage] = useState(isStringNotEmpty(_language) ? _language : "");
    const [audience, setAudience] = useState(isStringNotEmpty(_audience) ? _audience : "");
    const [type,setType] = useState(isStringNotEmpty(_type) ? _type : "");
    const [errors, setErrors] = useState({ title: false });

    const mapLang = (lang) => {
        if (lang === "en") {
            return "English";
        }
        if (lang === "fr") {
            return "French";
        }
        return "English";
    };

    useEffect(() => {
        setLocalState({
            document: selectedDocument,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDocument]);
    
    useEffect(() => {
        const noTitle = !localState?.document?.title; 
        const newErrors = {
            title: noTitle,
        };
        setErrors(newErrors);
        const isValid = !newErrors.title;
        setIsFieldValid(isValid);
        setHasChanges(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [localState?.document?.title, setIsFieldValid]);
    
    const updateDocument = (change) => {
        const updatedDocument = {
            ...localState?.document,
            ...change
        };
        setDocument(updatedDocument);
        setLocalState({
            document: updatedDocument,
        });
    };
    
    const onChange = (value, property) => {
        const change = {
            [property]: value
        };
        updateDocument(change);
        setHasChanges(true);
    };
    
    const onChangeAudience = (event) => {
        const value = event.target.value;
        setAudience(value);
        onChange(value, "audience");
    };
    
    const onChangeLanguage = (event) => {
        const value = event.target.value;
        setLanguage(value);
        onChange(value, "language");
    };
    
    const onChangeTitle = (event) => {
        const value = event.target.value;
        setTitle(value);
        onChange(value, "title");
    };
    
    const onChangeType = (event) => {
        const value = event.target.value;
        setType(value);
        onChange(event.target.value, "type");
    };
    
    const onChangeDescription = (event) => {
        const value = event.target.value;
        setDescription(value);
        onChange(event.target.value, "description");
    };
    
    const onChangeLinkText = (event) => {
        const value = event.target.value;
        setLinkText(value);
        onChange(event.target.value, "link_text");
    };
    
    return (
        <Fragment>
            <TabPanel value={selectedDocEditTab} index={0}>
                {hasDocument && (
                    <>
                        <LabelledField label={t("commons.title")} isRequired={true} className='labelField document-edit-panel' tooltip={t("commons.titleTooltip")}>
                            {/*<pre style={{fontSize: 9}}>*/}
                            {/*    {JSON.stringify(errors, null, 2)}*/}
                            {/*    {JSON.stringify(localState, null, 2)}*/}
                            {/*</pre>*/}
                            <TextField
                                onChange={onChangeTitle}
                                id='document-title'
                                fullWidth
                                variant="outlined"
                                defaultValue={title}
                                error={errors.title}
                            />
                        </LabelledField>
                        <LabelledField label={t("commons.description")} className='labelField document-edit-panel'>
                            <TextField
                                onChange={onChangeDescription}
                                id='document-description'
                                fullWidth
                                multiline={true}
                                variant="outlined"
                                defaultValue={description}
                                placeholder="Description"
                            />
                        </LabelledField>
                        <LabelledField label={t("commons.linkText")} className='labelField document-edit-panel' tooltip={t("commons.linkTextTooltip")}>
                            <TextField
                                onChange={onChangeLinkText}
                                id='document-link-text'
                                fullWidth
                                placeholder='Add link text'
                                variant="outlined"
                                defaultValue={linkText}
                            />
                        </LabelledField>
                        <LabelledField label={t("commons.category")} className='labelField document-edit-panel'>
                            <Select
                                id='document-type'
                                fullWidth
                                variant="outlined"
                                value={type}
                                placeholder='Select a category'
                                onChange={onChangeType}
                                displayEmpty
                                renderValue={(selected) => {
                                    if (selected.length === 0) {
                                        return <span className="select-placeholder-css">Select a category</span>;
                                    }
                                    return selected;
                                }}
                            >
                                <MenuItem value='Annual Report'>Annual Report</MenuItem>
                                <MenuItem value='Application Form'>Application Form</MenuItem>
                                <MenuItem value='Bylaw'>Bylaw</MenuItem>
                                <MenuItem value='Committee Report'>Committee Report</MenuItem>
                            </Select>
                        </LabelledField>
                        <LabelledField label={t("commons.language")} className='labelField document-edit-panel'>
                            <Select
                                id='document-language'
                                fullWidth
                                variant="outlined"
                                value={language}
                                placeholder='Select a language'
                                onChange={onChangeLanguage}
                                displayEmpty
                                renderValue={(selected) => {
                                    if (selected.length === 0) {
                                        return <span className="select-placeholder-css">Select a language</span>;
                                    }
                                    return mapLang(selected);
                                }}
                            >
                                <MenuItem value='en'>English</MenuItem>
                                <MenuItem value='fr'>French</MenuItem>
                            </Select>
                        </LabelledField>
                        <LabelledField label={t("commons.audience")} className='labelField document-edit-panel'>
                            <Select
                                id='document-audience'
                                fullWidth
                                variant="outlined"
                                value={audience}
                                placeholder='Add an audience'
                                onChange={onChangeAudience}
                                displayEmpty
                                renderValue={(selected) => {
                                    if (selected.length === 0) {
                                        return <span className="select-placeholder-css">Add an audience</span>;
                                    }
                                    return selected;
                                }}
                            >
                                <MenuItem value='Business Owners'>Business Owners</MenuItem>
                                <MenuItem value='General'>General</MenuItem>
                                <MenuItem value='Property Developers'>Property Developers</MenuItem>
                                <MenuItem value='Visitors'>Visitors</MenuItem>
                            </Select>
                        </LabelledField>
                    </>
                )}
            </TabPanel>
        </Fragment>
    );
};
export { DocumentEditPropertiesPanel };
